import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft,
  Lightbulb,
  Sparkles,
  Bug,
  Loader2,
  Send,
} from 'lucide-react';
import { api } from '../../lib/api';
import { RichTextEditor } from '../../components/editor/RichTextEditor';
import { useUIStore } from '../../stores/uiStore';

interface Project {
  id: string;
  name: string;
}

const typeOptions = [
  {
    value: 'idea',
    label: 'Idea',
    description: 'A new concept or suggestion',
    icon: Lightbulb,
    color: 'text-amber-500',
    bg: 'bg-amber-500/10',
    border: 'border-amber-500/30',
  },
  {
    value: 'feature',
    label: 'Feature',
    description: 'A specific functionality request',
    icon: Sparkles,
    color: 'text-violet-500',
    bg: 'bg-violet-500/10',
    border: 'border-violet-500/30',
  },
  {
    value: 'bug',
    label: 'Bug',
    description: 'An issue or problem to fix',
    icon: Bug,
    color: 'text-red-500',
    bg: 'bg-red-500/10',
    border: 'border-red-500/30',
  },
];

export function NewRequestPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const projectIdFromUrl = searchParams.get('projectId');
  const { setSidebarCollapsed } = useUIStore();

  const [projects, setProjects] = useState<Project[]>([]);
  const [isLoadingProjects, setIsLoadingProjects] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [projectId, setProjectId] = useState(projectIdFromUrl || '');
  const [title, setTitle] = useState('');
  const [type, setType] = useState<'idea' | 'feature' | 'bug'>('idea');
  const [descriptionHtml, setDescriptionHtml] = useState('');

  // Collapse sidebar on mount, restore on unmount
  useEffect(() => {
    setSidebarCollapsed(true);
    return () => setSidebarCollapsed(false);
  }, [setSidebarCollapsed]);

  useEffect(() => {
    async function fetchProjects() {
      try {
        setIsLoadingProjects(true);
        const response = await api.get<Project[]>('/projects');
        setProjects(Array.isArray(response.data) ? response.data : []);
        if (!projectId && response.data.length > 0) {
          setProjectId(response.data[0].id);
        }
      } catch (err) {
        console.error('Failed to load projects:', err);
      } finally {
        setIsLoadingProjects(false);
      }
    }
    fetchProjects();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!projectId || !title.trim()) return;

    try {
      setIsSubmitting(true);
      setError(null);

      const response = await api.post<{ id: string }>('/requests', {
        projectId,
        title: title.trim(),
        type,
        descriptionHtml: descriptionHtml || undefined,
      });

      navigate(`/requests/${response.data.id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create request');
      setIsSubmitting(false);
    }
  };

  if (isLoadingProjects) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-accent" />
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col min-h-0">
      {/* Header Bar */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center justify-between mb-4"
      >
        <div className="flex items-center gap-4">
          <Link
            to="/requests"
            className="p-2 rounded-xl hover:bg-bg-secondary transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <h1 className="text-xl font-bold">Submit a Request</h1>
        </div>
      </motion.div>

      <motion.form
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        onSubmit={handleSubmit}
        className="flex-1 grid grid-cols-1 lg:grid-cols-6 gap-6 min-h-0"
      >
        {/* Left Column - Title + Editor (5/6) */}
        <div className="lg:col-span-5 flex flex-col min-h-0 order-2 lg:order-1">
          {/* Title */}
          <div className="mb-3">
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Request title..."
              className="w-full px-4 py-3 bg-bg-secondary border border-border rounded-xl text-text text-lg placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-accent/50"
              required
            />
          </div>
          {/* Editor */}
          <div className="flex-1 min-h-0">
            <RichTextEditor
              content={descriptionHtml}
              onChange={setDescriptionHtml}
              placeholder="Describe your request in detail... You can paste or drag images here and annotate them by clicking on them."
              className="h-full"
            />
          </div>
        </div>

        {/* Right Column - Form Controls (1/6) */}
        <div className="lg:col-span-1 space-y-4 order-1 lg:order-2">
          {/* Project Selection */}
          <div>
            <label className="block text-sm font-medium mb-2">Project</label>
            <select
              value={projectId}
              onChange={(e) => setProjectId(e.target.value)}
              className="w-full px-3 py-2 bg-bg-secondary border border-border rounded-xl text-text text-sm focus:outline-none focus:ring-2 focus:ring-accent/50"
              required
            >
              <option value="">Select...</option>
              {projects.map((project) => (
                <option key={project.id} value={project.id}>
                  {project.name}
                </option>
              ))}
            </select>
          </div>

          {/* Type Selection */}
          <div>
            <label className="block text-sm font-medium mb-2">Type</label>
            <div className="space-y-2">
              {typeOptions.map((option) => {
                const Icon = option.icon;
                const isSelected = type === option.value;
                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => setType(option.value as typeof type)}
                    className={`w-full p-2.5 rounded-xl border-2 transition-all text-left flex items-center gap-2 ${
                      isSelected
                        ? `${option.border} ${option.bg}`
                        : 'border-border hover:border-border-light bg-bg-secondary'
                    }`}
                  >
                    <Icon className={`w-4 h-4 flex-shrink-0 ${option.color}`} />
                    <span className="font-medium text-sm truncate">{option.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Error */}
          {error && (
            <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-xl text-red-500 text-sm">
              {error}
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex flex-col gap-2 pt-2">
            <button
              type="submit"
              disabled={isSubmitting || !projectId || !title.trim()}
              className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-accent hover:bg-accent-dark disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-xl transition-colors font-medium text-sm"
            >
              {isSubmitting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Send className="w-4 h-4" />
              )}
              Submit
            </button>
            <Link
              to="/requests"
              className="w-full px-4 py-2.5 text-center text-text-secondary hover:text-text text-sm transition-colors"
            >
              Cancel
            </Link>
          </div>
        </div>
      </motion.form>
    </div>
  );
}
